import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { OUTPUT_TTL } from '../../constants/Pac';
import { DeviceTypes } from '../../constants/DeviceModels';
import { beltSwitch } from './device-switches/belt-switch';
import {
  holdDischarge as _holdDischarge,
  resumeDischarge as _resumeDischarge,
  finishDischargeScaleEarlier as _finishDischargeScaleEarlier,
} from './discharge-operations/discharge-control';
import AbortBatch from '../../containers/Batch/AbortBatch';
import { detectContamination } from '../../components/Contamination/detect-contamination';
import React from 'react';
import { showConfirmModal } from '../../modules/modals/confirmService';
import type {
  IBin,
  IDevice,
  IMixer,
  IPump,
  IScale,
  ISwitch,
} from '../../../types/device/DeviceModels';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';
import type { IMaterial } from '../../../types/material/MaterialModels';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import { ConfirmWithInitials } from '../../components/Confirm/ConfirmWithInitials';
import { BatchEventType } from '../../constants/BatchEventType';
import { MaterialPosition } from '../../constants/MaterialModels';
import { isFeatureEnabled } from '../../utils/feature-flags';

const FEATURE_OVER_TOLERANCE_POPUP = isFeatureEnabled('over_tolerance_popup');

export const controlMixin = {

  onFlowRateDown(this: IBatchStore, scale: IScale) {

    const d = this.getDeviceStatus(scale.id);

    if (d?.status !== DeviceStatus.DISCHARGING) {
      return;
    }

    this.deviceStatus.set(scale.id, {
      ...d,
      flowRate: d.flowRate > 0 ? toNumber(d.flowRate - 1, 1) : 0,
    });
  },

  onFlowRateUp(this: IBatchStore, scale: IScale): void {

    const d = this.getDeviceStatus(scale.id);
    const maxFlowRate = 500;

    if (d?.status !== DeviceStatus.DISCHARGING) {
      return;
    }

    this.deviceStatus.set(scale.id, {
      ...d,
      flowRate: d.flowRate <  maxFlowRate ? toNumber(d.flowRate + 1, 1) : 0,
    });
  },

  flowRateUp(this: IBatchStore) {

    this.devicesStore.scales.forEach((scale: IScale) => this.onFlowRateUp(scale));
  },

  flowRateDown(this: IBatchStore) {

    this.devicesStore.scales.forEach((scale: IScale) => this.onFlowRateDown(scale));
  },

  onFeedFlowRateChange(
    this: IBatchStore,
    scale: IScale,
    onChange: (flowRate: number) => void = () => {/* empty */},
  ): void {

    const w1 = this.getScaleWeight(scale);

    this.setTimeout(() => {
      const w2 = this.getScaleWeight(scale);
      const flowRate = w2 - w1;

      onChange(flowRate);

    }, 1000);
  },

  overrideDevice(this: IBatchStore, deviceId: number | string): void {
    if (!deviceId) {
      return;
    }

    this.clearDeviceError(deviceId);
    const ds = this.getDeviceStatus(deviceId);
    ds.errorsOverridden.push(ds.status);

    this.timers.set(timerKey.overrideDevice(deviceId), () => {
      this.deviceStatus.set(deviceId, { ...this.getDeviceStatus(deviceId), overridden: false });
    }, 5000);
  },

  override(this: IBatchStore) {
    this.saveLog('Key pressed OVERRIDE');
    const id = this.clearDeviceError();

    if (!this.running.length) {
      return;
    }

    this.isOverride = true;

    if (id) {
      const ds = this.getDeviceStatus(id);
      ds.errorsOverridden.push(ds.status);
    }

    this.timers.set(timerKey.override(), () => {
      this.isOverride = false;

      if (id) {
        this.deviceStatus.set(id, { ...this.getDeviceStatus(id), overridden: false });
      }
    }, 5000);
  },

  demo(this: IBatchStore) {

    logger.log('demo');
    if (this.running.length) {
      this.showWarning('Can\'t switch DEMO while Batching!');
      return;
    }

    if (this.rootStore.diagnoseStore.isTesting) {
      this.showWarning('Can\'t switch DEMO while running Diagnose Auto Test!');
      return;
    }

    if (this.jogTestRunning) {
      this.showWarning('Can\'t switch DEMO while running Auto Jog Test!');
      return;
    }

    this.pacSimulator.reset();
    this.pacSimulator.init();

    this.timers.clear(timerKey.demo());

    this.timers.set(timerKey.demo(), () => {
      this.isDemo = !this.isDemo;

      this.timers.clear(timerKey.pacInitial());
      this.injectCode(this.executeCode);

      if (this.isDemo) {
        this.pacSimulator.reset();
        this.start();
      } else {
        this.checkIndicatorError();

        this.sendPacInitialCommands();

      }

    }, 300);

  },

  checkScalesAndClose(this: IBatchStore) {

    if (!this.running.length && this.pacConnected) {
      this.devicesStore.scales.forEach((scale: IScale) => {
        if (this.isInputDisabled(scale.gateOneStatusPNo)) {
          this.closeScale(scale);
        }
        if (this.isDoubleGateScale(scale) && this.isInputDisabled(scale.gateTwoStatusPNo)) {
          this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        }
      });
    }
  },

  closeBin(this: IBatchStore, bin: IBin) {

    if (!bin) {
      return;
    }

    const isOpen = this.isOutputOn(bin.gateOnePNo);

    if (isOpen) {
      this.setOutput(bin.gateOnePNo, '0');
    }
  },

  holdBin(this: IBatchStore, bin: IBin) {

    this.closeBin(bin);

    const d = this.getDeviceStatus(bin.id);
    this.deviceStatus.set(bin.id, {
      ...d,
      status: DeviceStatus.HOLD,
    });
  },

  removeLoad(this: IBatchStore, batchDataId: number, drop: number = 1) {

    const batchData = this.running.find(v => v.id === batchDataId && v.currentDrop === drop);

    if (!batchData) {
      return;
    }

    showConfirmModal({
      title: 'Remove the load?',
      message: `Are you sure you want to remove the load ${batchData.tId} ${batchData.currentDrop}/${batchData.allDrops}`,
      okLabel: 'Yes',
      cancelLabel: 'No',
      enableEscape: false,
    }).then(() => {
      if (this.canSendRemoteInstruction()) {
        this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.REMOVE_LOAD, null, {
          id: batchDataId,
          drop,
        }));
        return;
      }

      this.setTimeout(() => {
        this.executeRemoveLoad(batchDataId, drop);
      }, 1000);

    }).catch(() => {
      logger.log('no');
    });
  },

  holdFeed(this: IBatchStore) {

    this.saveLog('Key pressed HOLD FEED');

    if (this.isHoldFeed && !this.isEmergencyStop) {
      this.resumeFeed();
      return;
    }

    this.isHoldFeed = true;

    this.deviceStatus.forEach(v => {
      if (!this.holdFeedDevices.includes(v.id) && (v.status === DeviceStatus.FEED || v.status === DeviceStatus.JOG)
        && (v.type === DeviceTypes.Bin.toUpperCase() || v.type === DeviceTypes.Pump.toUpperCase())) {
        this.holdFeedDevices.push(v.id);
        if (v.type === DeviceTypes.Bin.toUpperCase()) {
          this.setFeedInterruption(v.id);
        }
      }
    });
  },

  resumeFeed(this: IBatchStore) {

    this.isHoldFeed = false;

    if (this.trackedFeedPumps.length) {
      this.trackedFeedPumps.forEach(id => this.resumeFeedPump(id));
    }
    this.holdFeedDevices = [];
    this.pressedBinButtons.clear();
  },

  isHoldScale(this: IBatchStore, scaleId: number) {

    return this.trackedDischargeScales.find(id => id === scaleId);
  },

  resumeScaleId(this: IBatchStore, scaleId: number) {

    if (!scaleId || !this.canEmit || this.replayMode) {
      return;
    }

    const scale = this.devicesStore.scales.find((v: IScale) => v.id === Number(scaleId));

    if (scale) {
      if (scale.decumulative) {
        this.unHoldFeedDevice(scale.id);
      } else {
        this.resumeScale(scale.id);
      }
    }
  },

  holdScaleId(this: IBatchStore, scaleId: number) {

    if (!scaleId || !this.canEmit || this.replayMode) {
      return;
    }

    const scale = this.devicesStore.scales.find((v: IScale) => v.id === Number(scaleId));

    if (scale) {
      if (scale.decumulative) {
        this.holdFeedDevice(scale.id);
      } else {
        this.holdScale(scale);
      }
    }
  },

  holdScale(this: IBatchStore, scale: IScale) {

    if (this.canSendRemoteInstruction() || scale.decumulative) {
      return;
    }

    const d = this.getDeviceStatus(scale.id);

    if (d.status !== DeviceStatus.HOLD) {
      this.closeScale(scale);

      if (this.isDoubleGateScale(scale)) {
        this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
      }

      this.deviceStatus.set(scale.id, {
        ...d,
        status: DeviceStatus.HOLD,
      });
    }

    if (!this.isHoldScale(scale.id)) {
      this.trackedDischargeScales.push(scale.id);
    }
  },

  getInitialOpenTimeForScale(this: IBatchStore, scale: IScale, noFlowControl?: boolean): number|string {

    let initialOpenTime: string | number = OUTPUT_TTL;

    if (!noFlowControl && (
      (scale.closeGateOnePNo && !scale.switchGateTypePNo)
      || (scale.closeGateOnePNo && scale.switchGateTypePNo && this.isScaleSwitchGatePortOn(scale))
    )) {
      initialOpenTime = (scale.dischargeOpen || 0) * 1000;
    }

    return initialOpenTime;
  },

  resumeScale(this: IBatchStore, scaleId: number) {

    if (this.canSendRemoteInstruction()) {
      return;
    }

    const scale = this.devicesStore.getDeviceById(scaleId);

    this.timers.clear(timerKey.beltResume(scale.beltId));

    if (scale.beltId && !this.isBeltOn(scale.beltId)) {
      const belt = this.devicesStore.getDeviceById(scale.beltId);
      const time = belt ? (belt.onDelay || 1) * 1000 : 1000;

      beltSwitch(this.rootStore, scale.beltId, true);

      logger.log(`resume scale #${scale.id}, wait for belt`, scale.beltId);

      if (belt?.autoTurnOn === false) {
        this.showWarning('The scale is waiting for the belt to be turned on');
      }

      this.timers.set(timerKey.beltResume(scale.beltId), () => {
        this.resumeScale(scaleId);
      }, time + 100);

      return;
    }

    const scaleStatus = this.getDeviceStatus(scaleId);
    const runningData = this.running?.find(v => v.uid === scaleStatus.uid);
    const noFlowControl = scale.noFlowControl || runningData?.sequence?.noFlowControl;

    if (scale && !this.isScaleOpen(scale)) {
      this.openScale(scale, this.getInitialOpenTimeForScale(scale, noFlowControl));
    }

    if (this.isDoubleGateScale(scale) && !this.isScaleOpen(scale, scale.gateTwoStatusPNo)) {
      this.openScale(scale, this.getInitialOpenTimeForScale(scale, noFlowControl), scale.openGateTwoPNo, scale.closeGateTwoPNo);
    }

    this.deviceStatus.set(scaleId, {
      ...scaleStatus,
      status: DeviceStatus.DISCHARGING,
    });

    this.trackedDischargeScales = this.trackedDischargeScales.filter(id => id !== scaleId);
  },

  finishDischargeScaleEarlier(this: IBatchStore, scaleId: number) {

    return _finishDischargeScaleEarlier(this, scaleId);
  },

  holdDischarge(this: IBatchStore) {

    return _holdDischarge(this);
  },

  resumeDischarge(this: IBatchStore) {

    return _resumeDischarge(this);
  },

  sendAbortCommands(this: IBatchStore) {

    const { scales, pumps, mixers, switches } = this.devicesStore;
    const excluded: number[] = [];

    scales.forEach((scale: IScale) => {
      const s = this.getDeviceStatus(scale.id);
      if (s.status === DeviceStatus.FEED) {
        this.resetWeight(scale.inputPNo);
      }

      if (scale.decumulative) {
        const materials = this.materialStore.materials.filter((m: IMaterial) => m.scaleId === scale.id) || [];
        materials.forEach((material: IMaterial) => {
          const scalePorts = this.getScalePorts(scale, material);
          this.setOutput(scalePorts.closePort, '!');
        });
      } else if (scale.closeGateOnePNo) {
        this.setOutput(scale.closeGateOnePNo, '!');
      }

    });

    pumps.forEach((pump: IPump) => {
      const s = this.getDeviceStatus(pump.id);
      if (s.status === DeviceStatus.FEED) {
        this.resetMeter(pump.meterPNo);
      }
    });

    scales.forEach((scale: IScale) => {
      if (scale.closeGateOnePNo) {
        excluded.push(scale.closeGateOnePNo);
      }
      if (scale.closeGateTwoPNo) {
        excluded.push(scale.closeGateTwoPNo);
      }
      if (scale.closeGateThreePNo) {
        excluded.push(scale.closeGateThreePNo);
      }
    });

    mixers.forEach((mixer: IMixer) => {
      if (mixer.mixerOnPNo) {
        excluded.push(mixer.mixerOnPNo);
      }
      if (mixer.openTiltPNo) {
        excluded.push(mixer.openTiltPNo);
      }
      if (mixer.closeReturnPNo) {
        excluded.push(mixer.closeReturnPNo);
      }
      if (mixer.diverterPNo) {
        excluded.push(mixer.diverterPNo);
      }
    });

    switches.forEach((v: ISwitch) => {
      if (v.emergencyExemption) {
        excluded.push(v.onPNo, v.offPNo);
      }
    });

    this.setTimeout(() => {
      this.closeOpenedOutputs(excluded);
    }, 300);
  },

  abort(this: IBatchStore) {
    logger.log('abort');
    this.saveLog('Key pressed ABORT');

    if (this.isAbortDialogVisible || !this.running.length) {
      return;
    }

    this.isAbortDialogVisible = true;

    showConfirmModal({
      title: 'Abort the Batch?',
      message: <AbortBatch onChange={(value: any) => this.abortWithQueue = value}/>,
      okLabel: 'Yes',
      cancelLabel: 'No',
      enableEscape: false,
      onExited: () => {
        this.isAbortDialogVisible = false;
      },
    }).then(() => {
      this.executeAbort();
    }).catch(() => {
      this.isAbort = false;
    });
  },

  onContaminationChange(this: IBatchStore, e: any, data: any) {
    this.contaminationData = {
      eventType: BatchEventType.CONTAMINATION,
      author: e.target.value,
      time: new Date().toISOString(),
      ...data,
    };
    logger.log('on contamination initial change', this.contaminationData);
  },

  onOverToleranceChange(this: IBatchStore, e: any, data: any) {
    this.overToleranceData = {
      eventType: BatchEventType.OVER_TOLERANCE,
      author: e.target.value,
      time: new Date().toISOString(),
      ...data,
    };
    logger.log('on initials confirm', this.overToleranceData);
  },

  isContaminationDetected(this: IBatchStore, output: string|number, value: string|number): boolean {
    if (!this.settingsStore.configuration?.showContaminationAlert) {
      return false;
    }

    if (!this.canEmit || this.replayMode || this.maintenanceMode || !this.running.length) {
      this.contaminationData = null;
      return false;
    }

    if (Number(value) === 0) {
      return false;
    }

    output = Number(output);
    const outputsToCheck = this.getContaminationOutputsToCheck();
    logger.log(output, value, outputsToCheck);

    if (!outputsToCheck.includes(output)) {
      return false;
    }

    const data = this.getContaminationDataFromOutput(output);

    if (!data) {
      return false;
    }

    if (this.running[0]?.events.find((v: any) => v.output === output)) {
      logger.log('contamination allowed', output, data);
      return false;
    }

    logger.log('contamination', data.material, output, data);

    this.showContaminationPopup(data);

    return true;
  },

  showContaminationPopup(this: IBatchStore, data: any): void {
    showConfirmModal({
      title: `Contamination ${data.material}`,
      message: <ConfirmWithInitials
        onConfirm={e => this.onContaminationChange(e, data)}
        text={'Please enter your initials to allow contamination'}
      />,
      okLabel: 'Allow',
      cancelLabel: 'Cancel',
      enableEscape: false,
    })
      .then(() => {
        if (
          this.running.length
          && this.contaminationData?.author
          && !this.running[0].events.some((v: any) => v.output === this.contaminationData?.output)
        ) {
          this.running[0].events.push(this.contaminationData);
        }
      })
      .catch(() => {
        this.contaminationData = null;
        logger.log('user cancel contamination');
      });
  },

  isModalVisible(): boolean {
    return document?.getElementsByClassName('confirm-modal').length > 0;
  },

  showOverTolerancePopup(this: IBatchStore, data: any): void {
    logger.log('over tolerance popup feature on:', FEATURE_OVER_TOLERANCE_POPUP, this.isModalVisible(), data);

    if (
      !FEATURE_OVER_TOLERANCE_POPUP
      || this.isModalVisible()
      || !this.settingsStore.configuration?.showPopupOnOverweight
    ) {
      return;
    }

    const runningData = this.running.find(v => v.uid === data.uid && v.currentDrop === data.currentDrop);

    if (!runningData || runningData?.events.some((v: any) => v.material === data.material)) {
      logger.log('over tolerance initials exist', data.material);
      return;
    }

    showConfirmModal({
      title: `Material ${data.material} is over target by ${data.weight - data.target} ${data.unit || ''}`,
      message: <ConfirmWithInitials
        onConfirm={e => this.onOverToleranceChange(e, data)}
        text={'Please enter your initials to confirm the change'}
      />,
      okLabel: 'Accept',
      cancelLabel: 'Cancel',
      enableEscape: false,
    })
      .then(() => {
        if (
          this.running.length
          && this.overToleranceData?.author
          && !runningData?.events.some((v: any) => v.material === this.overToleranceData?.material)
        ) {
          runningData?.events.push(this.overToleranceData);
          this.overToleranceData = null;
          this.overrideDevice(data.deviceId);
        }
      })
      .catch(() => {
        this.overToleranceData = null;
        logger.log('user cancel over tolerance popup', data);
      });
  },

  getContaminationDataFromOutput(this: IBatchStore, output: number): any {
    const device = this.devicesStore.devices
      .find((d: IBin | IPump | IScale | any) => d.gateOnePNo === output
        || d.gatePNo === output
        || d.openGateOnePNo === output
        || d.openGateTwoPNo === output
        || d.openGateThreePNo === output
        || d.openGateOneAdditionalPNo === output
        || d.openGateTwoAdditionalPNo === output
        || d.openGateThreeAdditionalPNo === output);
    let material = this.materialStore.getMaterialForBin(device?.id) || this.materialStore.materials.find((m: IMaterial) => m.pumpId === device?.id);
    if (device && !material) {
      if (device.openGateOnePNo === output || device.openGateOneAdditionalPNo === output) {
        material = this.materialStore.materials.find((m: IMaterial) => m.scaleId === device?.id && m.position === MaterialPosition.One);
      } else if (device.openGateTwoPNo === output || device.openGateTwoAdditionalPNo === output) {
        material = this.materialStore.materials.find((m: IMaterial) => m.scaleId === device?.id && m.position === MaterialPosition.Two);
      } else if (device.openGateThreePNo === output || device.openGateThreeAdditionalPNo === output) {
        material = this.materialStore.materials.find((m: IMaterial) => m.scaleId === device?.id && m.position === MaterialPosition.Three);
      }
    }

    logger.log('contamination:', output, device?.description, material?.code);

    if (!material || !this.running.length || !this.running[0].mixDesign) {
      return;
    }

    const loadWithMaterial = this.running[0].mixDesign?.find((v: MixDesignItem) => v.constituentItemId === material?.itemId);

    if (loadWithMaterial) {
      return;
    }

    const materialName = this.materialStore.getMaterialName(material);

    return {
      material: materialName,
      deviceId: device?.id,
      ticketId: this.running[0]?.id,
      output,
    };
  },

  getContaminationOutputsToCheck(this: IBatchStore): number[] {
    const binOutputs = this.devicesStore.bins
      .filter((b: IBin) => b.gateOnePNo)
      .map((b: IBin) => b.gateOnePNo);
    const decumOutputs = this.devicesStore.scales
      .filter((s: IScale) => s.decumulative)
      .flatMap((s: IScale) =>
        Object.entries(s)
          .filter(([key, value]) => key.startsWith('openGate') && value)
          .map(([, value]) => value),
      );
    const pumpOutputs = this.devicesStore.pumps
      .filter((p: IPump) => p.gatePNo)
      .map((p: IPump) => p.gatePNo);

    return [...binOutputs, ...pumpOutputs, ...decumOutputs];
  },

  checkContamination(this: IBatchStore, newOutputs: number[], oldOutputs: number[]) {

    if (!this.settingsStore.configuration?.showContaminationAlert) {
      return;
    }

    if (!this.canEmit || this.replayMode || this.maintenanceMode || !this.running.length) {
      this.contaminationData = null;
      return;
    }

    const outputsToCheck = this.getContaminationOutputsToCheck();

    detectContamination({
      newOutputs,
      oldOutputs,
      outputsToCheck,
      onContamination: (output) => {
        const data = this.getContaminationDataFromOutput(output);

        if (!data) {
          return;
        }

        if (this.running[0]?.events.find((v: any) => v.output === output)) {
          logger.log('contamination allowed', output, data);
          return;
        }

        logger.log('contamination', data.material, output, data);

        //close the gate
        this.setOutput(output, 0);

        this.showContaminationPopup(data);
      },
    });
  },

  resumeStoppedDevices(this: IBatchStore) {

    let data = this.stoppedDevices.map(v => {
      return { port: v.port, value: v.value ? 0 : OUTPUT_TTL };
    });

    logger.log('resume all devices', data);
    this.setOutputs(data);
    this.stoppedDevices = [];
  },

  emergencyStop(this: IBatchStore) {

    this.saveLog('Key pressed STOP');
    this.isEmergencyStop = !this.isEmergencyStop;
    this.holdFeed();
    this.holdDischarge();

    if (!this.isEmergencyStop) {
      this.resumeStoppedDevices();
      return;
    }

    const { devices } = this.devicesStore;
    const devicesToStop = devices.filter((v: IDevice) => !v.emergencyExemption);
    let data: any[] = [];

    devicesToStop.forEach((device: IDevice | any) => {
      switch(device.deviceTypeName) {
        case DeviceTypes.Scale:
        case DeviceTypes.Pump:
        case DeviceTypes.Bin:
          // handled by holdFeed & holdDischarge
          break;
        case DeviceTypes.Boot:
          if (this.isOutputOn(device.upPNo)) {
            data.push({ port: device.upPNo, value: 0 }, { port: device.downPNo, value: OUTPUT_TTL });
          }
          break;
        case DeviceTypes.Mixer:
          if (this.isOutputOn(device.openGatePNo)) {
            data.push({ port: device.openGatePNo, value: 0 }, { port: device.closeReturnPNo, value: OUTPUT_TTL });
          }
          break;
        case DeviceTypes.Switch:
          if (this.isOutputOn(device.onPNo)) {
            data.push({ port: device.onPNo, value: 0 }, { port: device.offPNo, value: OUTPUT_TTL });
          }
          break;
        case DeviceTypes.Tank:
          if (this.isOutputOn(device.gatePNo)) {
            data.push({ port: device.gatePNo, value: 0 });
          }
          break;
        case DeviceTypes.Belt:
          if (this.isOutputOn(device.onPNo)) {
            data.push({ port: device.onPNo, value: 0 });
          }
          break;
        case DeviceTypes.Aerator:
        case DeviceTypes.DustCollector:
        case DeviceTypes.Horn:
          if (this.isOutputOn(device.switchPNo)) {
            data.push({ port: device.switchPNo, value: 0 });
          }
          break;
        case DeviceTypes.Vibrator:
          if (this.isOutputOn(device.switchOnePNo)) {
            data.push({ port: device.switchOnePNo, value: 0 });
          }
          break;
        default:
      }
    });

    logger.log('emergency stop', devicesToStop, data);
    this.stoppedDevices = data;
    this.setOutputs(data);
  },

};
